(() => {
  const init = () => {
    const root = document.querySelector('[data-nobs-landing-root]');
    if (!root || document.documentElement.dataset.nobsLpReady === 'true') return;

    document.documentElement.dataset.nobsLpReady = 'true';
    document.body.classList.add('nobs-landing-page');

    const emit = (eventName, detail = {}) => {
      const payload = { event: eventName, ...detail };
      window.dispatchEvent(new CustomEvent(`nobs:${eventName}`, { detail: payload }));
      window.dispatchEvent(new CustomEvent('nobs:interaction', { detail: payload }));
    };

    document.addEventListener('click', (event) => {
      const target = event.target.closest('[data-nobs-event]');
      if (!target) return;
      emit(target.dataset.nobsEvent, {
        label: target.dataset.nobsLabel || target.textContent.trim().replace(/\s+/g, ' ').slice(0, 120),
        section: target.closest('[data-nobs-section]')?.dataset.nobsSection || null
      });
    });

    document.querySelectorAll('[data-nobs-product-form]').forEach((form) => {
      form.addEventListener('submit', () => {
        const variant = form.querySelector('[name="id"]')?.value || null;
        const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
        const sellingPlan = form.querySelector('[name="selling_plan"]:not([disabled])')?.value || null;
        emit('add_to_cart', { variant, quantity, sellingPlan });
      });
    });

    document.querySelectorAll('[data-nobs-quantity-option]').forEach((option) => {
      option.addEventListener('change', () => {
        const scope = option.closest('[data-nobs-offer]');
        const input = scope?.querySelector('[data-nobs-quantity-input]');
        if (input) input.value = option.value;
        emit('offer_select', { quantity: Number(option.value), label: option.dataset.nobsLabel || null });
      });
    });

    document.querySelectorAll('[data-nobs-purchase-option]').forEach((option) => {
      option.addEventListener('change', () => {
        if (!option.checked) return;
        const form = option.closest('[data-nobs-product-form]');
        const quantityInput = form?.querySelector('[data-nobs-quantity-input]');
        const sellingPlanInput = form?.querySelector('[data-nobs-selling-plan-input]');
        const sellingPlan = option.dataset.nobsSellingPlan || '';

        if (quantityInput) quantityInput.value = option.dataset.nobsQuantity || '1';
        if (sellingPlanInput) {
          sellingPlanInput.value = sellingPlan;
          sellingPlanInput.disabled = sellingPlan === '';
        }

        emit('offer_plan_select', {
          plan: option.value,
          quantity: Number(option.dataset.nobsQuantity || 1),
          sellingPlan: sellingPlan || null,
          label: option.dataset.nobsLabel || null
        });
      });
    });

    document.querySelectorAll('[data-nobs-gallery]').forEach((gallery) => {
      const track = gallery.querySelector('[data-nobs-gallery-track]');
      if (!track) return;
      gallery.querySelectorAll('[data-nobs-gallery-direction]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.nobsGalleryDirection === 'next' ? 1 : -1;
          track.scrollBy({ left: direction * track.clientWidth * 0.86, behavior: 'smooth' });
          emit('gallery_interaction', { direction: direction > 0 ? 'next' : 'previous' });
        });
      });
    });

    document.querySelectorAll('[data-nobs-faq]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        emit('faq_open', { question: details.querySelector('summary')?.textContent.trim() || null });
      });
    });

    const sticky = document.querySelector('[data-nobs-sticky]');
    const offer = document.querySelector('[data-nobs-offer]');
    if (sticky) {
      const visibleBlockers = new Set();
      const updateSticky = () => {
        const scrolled = window.scrollY > Math.max(420, window.innerHeight * 0.7);
        const consent = Array.from(document.querySelectorAll('[role="alertdialog"], .shopify-pc__banner__dialog, #shopify-pc__banner')).some((el) => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden';
        });
        const visible = scrolled && visibleBlockers.size === 0 && !consent;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
        sticky.toggleAttribute('inert', !visible);
      };

      if ('IntersectionObserver' in window) {
        const blockerObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) visibleBlockers.add(entry.target);
            else visibleBlockers.delete(entry.target);
          });
          updateSticky();
        }, { threshold: 0.12 });
        [offer, document.querySelector('[data-nobs-section="final_cta"]'), document.querySelector('.nobs-lp-footer')]
          .filter(Boolean)
          .forEach((element) => blockerObserver.observe(element));
      }

      window.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky, { passive: true });
      updateSticky();
    }

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

// Progressive, opt-in components. Native forms, Appstle, and classic pages stay untouched.
(() => {
  if (typeof customElements === 'undefined') return;
  if (!customElements.get('nobs-review-carousel')) {
    customElements.define('nobs-review-carousel', class extends HTMLElement {
      connectedCallback() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => { if (this.isConnected) this.connectedCallback(); }, { once: true });
          return;
        }
        if (this.controller) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.slides = [...this.querySelectorAll('[data-nobs-review-slide]')];
        if (!this.slides.length) return;
        this.index = 0;
        this.viewport = this.querySelector('[data-nobs-review-viewport]');
        this.querySelector('[data-nobs-review-controls]').hidden = false;
        this.classList.add('is-enhanced');
        this.show = (index, announce = true) => {
          this.index = Math.max(0, Math.min(this.slides.length - 1, index));
          this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
          this.querySelector('[data-nobs-review-prev]').disabled = this.index === 0;
          this.querySelector('[data-nobs-review-next]').disabled = this.index === this.slides.length - 1;
          this.querySelectorAll('[data-nobs-review-index]').forEach((button, i) => {
            if (i === this.index) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
          });
          this.querySelector('[data-nobs-review-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
          const picker = this.querySelector('[data-nobs-review-picker]');
          if (picker) picker.value = String(this.index);
          this.resize();
          if (announce) window.dispatchEvent(new CustomEvent('nobs:review_select', { detail: { index: this.index + 1 } }));
        };
        this.resize = () => { this.viewport.style.height = `${Math.ceil(this.slides[this.index].getBoundingClientRect().height)}px`; };
        this.addEventListener('click', event => {
          if (event.target.closest('[data-nobs-review-prev]')) this.show(this.index - 1);
          if (event.target.closest('[data-nobs-review-next]')) this.show(this.index + 1);
          const dot = event.target.closest('[data-nobs-review-index]');
          if (dot) this.show(Number(dot.dataset.nobsReviewIndex));
        }, options);
        this.addEventListener('change', event => {
          if (event.target.matches('[data-nobs-review-picker]')) this.show(Number(event.target.value));
        }, options);
        this.addEventListener('keydown', event => {
          // Preserve native selection and assistive-technology shortcuts.
          if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.target.matches('select, input, textarea')) return;
          let next;
          if (event.key === 'ArrowLeft') next = this.index - 1;
          if (event.key === 'ArrowRight') next = this.index + 1;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.slides.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            this.show(next);
          }
        }, options);
        this.viewport.addEventListener('pointerdown', event => { this.touchStart = { x: event.clientX, y: event.clientY }; }, options);
        this.viewport.addEventListener('pointercancel', () => { this.touchStart = null; }, options);
        this.viewport.addEventListener('pointerup', event => {
          if (!this.touchStart || event.pointerType === 'mouse') return;
          const dx = event.clientX - this.touchStart.x;
          const dy = event.clientY - this.touchStart.y;
          if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
          this.touchStart = null;
        }, options);
        this.querySelectorAll('img').forEach(img => img.addEventListener('load', this.resize, options));
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(this.resize);
          this.slides.forEach(slide => this.observer.observe(slide));
        }
        window.addEventListener('resize', this.resize, options);
        this.addEventListener('shopify:block:select', event => {
          const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
          if (index >= 0) this.show(index);
        }, options);
        this.show(0, false);
      }
      disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.controller = null; }
    });
  }
  if (!customElements.get('nobs-ingredient-tabs')) {
    customElements.define('nobs-ingredient-tabs', class extends HTMLElement {
      connectedCallback() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => { if (this.isConnected) this.connectedCallback(); }, { once: true });
          return;
        }
        if (this.controller) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        const tabs = [...this.querySelectorAll('[role="tab"]')];
        const panels = [...this.querySelectorAll('.nobs-ingredient-panel')];
        this.querySelector('[role="tablist"]').hidden = false;
        const select = (index, focus = false) => {
          tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
          panels.forEach((panel, i) => { panel.hidden = i !== index; panel.setAttribute('role', 'tabpanel'); panel.tabIndex = 0; });
          if (focus) tabs[index].focus();
        };
        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => select(index), options);
          tab.addEventListener('keydown', event => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next !== undefined) { event.preventDefault(); select(next, true); }
          }, options);
        });
        this.addEventListener('shopify:block:select', event => {
          const index = panels.findIndex(panel => panel === event.target || panel.contains(event.target));
          if (index >= 0) select(index);
        }, options);
        select(0);
      }
      disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    });
  }
  if (!document.documentElement.dataset.nobsSubscriptionLinks) {
    document.documentElement.dataset.nobsSubscriptionLinks = 'true';
    document.addEventListener('click', event => {
      const reviewLink = event.target.closest('[data-nobs-review-jump]');
      if (reviewLink) document.querySelector('nobs-review-carousel')?.show?.(Number(reviewLink.dataset.nobsReviewJump));
      if (!event.target.closest('[data-nobs-subscription-cta]')) return;
      const subscription = document.querySelector('[data-nobs-purchase-option][value="subscription"]');
      if (subscription && !subscription.disabled) {
        subscription.checked = true;
        subscription.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
})();
